import React from 'react';
import type { PassportCard } from '../../services/api/passportCards';
import { passportKind, passportEventDate } from '@bilt/domain/passportRegistry';
/** The source adapter supplies facts; composition never manufactures them. */
export default function PassportTypeFront({
  card,
  image,
  logo,
  marks,
}: {
  card: PassportCard;
  image: React.ReactNode;
  logo: React.ReactNode;
  marks?: React.ReactNode;
}) {
  const kind = card.subject_type;
  const facts = card.subject?.facts || {};
  const fact = (key: string) => (facts[key] == null ? '' : String(facts[key]));
  const title = <h3>{card.title}</h3>;
  const subtitle = card.subject?.subtitle;
  const status = (
    <div className="ct-source-status">
      {['verified', 'partner', 'community_verified'].includes(fact('verification_status')) && (
        <span>{fact('verification_status').replaceAll('_', ' ')}</span>
      )}
      {fact('entry_status') && <span>{fact('entry_status')}</span>}
      {kind === 'club' && fact('member_count') !== '' && (
        <span>{fact('member_count')} members</span>
      )}
    </div>
  );
  const identity = (
    <div className="pe-identity">
      {title}
      {status}
    </div>
  );

  const photo = (
    <div className="pe-photo">
      {image}
      {marks}
    </div>
  );
  const label = (text: string) => <span className="pe-kicker">{text}</span>;
  if (!card.subject)
    return (
      <div className="pe-front pe-private">
        {label(
          card.earned_at || card.ownership_status === 'active'
            ? 'Collected record'
            : 'Unavailable subject'
        )}
        {title}
        <p>Subject details are no longer public.</p>
      </div>
    );
  switch (kind) {
    case 'vehicle':
      return (
        <div className="pe-front pe-vehicle">
          <div className="pe-build-identity">
            {label([fact('year'), fact('make')].filter(Boolean).join(' ') || 'Vehicle')}
            {title}
            {subtitle && <p className="ct-subtitle">{subtitle}</p>}
          </div>
          <div className="ct-vehicle-photo">
            <div className="pe-photo">{image}</div>
            {marks}
          </div>
        </div>
      );
    case 'event':
      return (
        <div className="pe-front pe-event">
          {photo}
          <div className="pe-event-date">
            {label('Event date')}
            <strong>{passportEventDate(fact('start_at'), fact('timezone')) || '-'}</strong>
          </div>
          {identity}
          <p className="pe-event-venue">
            {[fact('venue'), fact('city')].filter(Boolean).join(' · ')}
          </p>
        </div>
      );
    case 'club':
      return (
        <div className="pe-front pe-club">
          <div className="pe-club-identity">
            {logo}
            {label('Community')}
            {identity}
            {fact('region') && <p>{fact('region')}</p>}
            {fact('focus') && <p>{fact('focus')}</p>}
          </div>
          {photo}
        </div>
      );
    case 'shop':
      return (
        <div className="pe-front pe-shop">
          <header>
            {logo}
            {label('Workshop record')}
            {title}
          </header>
          {photo}
          <div className="pe-shop-specialty">
            {status}
            {fact('specialties') && <strong>{fact('specialties')}</strong>}
            <p>{fact('city') || subtitle}</p>
          </div>
        </div>
      );
    case 'place':
      return (
        <div className={`pe-front pe-place pe-place-${fact('category')}`}>
          <div className="pe-place-tab">
            {label(fact('category').replaceAll('_', ' ') || 'Place')}
          </div>
          {photo}
          <div className="pe-postcard-caption">
            {title}
            <p>{fact('city') || subtitle}</p>
          </div>
        </div>
      );
    case 'booth':
      return (
        <div className="pe-front pe-booth">
          <div className="pe-exhibitor">
            {logo}
            {label(
              fact('campaign_title') || card.subject.collectible?.campaign_title || 'Exhibitor'
            )}
          </div>
          {photo}
          <div className="pe-booth-band">
            {title}
            {fact('zone') && <strong>{fact('zone')}</strong>}
          </div>
        </div>
      );
    case 'activation':
      return (
        <div className="pe-front pe-activation">
          {label(fact('category') || 'Experience')}
          {title}
          {photo}
          <div className="pe-activation-result">
            {fact('result') ? (
              <>
                <span>Recorded result</span>
                <strong>{fact('result')}</strong>
              </>
            ) : (
              <p>{subtitle || 'Discover the experience'}</p>
            )}
          </div>
        </div>
      );
    case 'destination':
      return (
        <div className="pe-front pe-destination">
          <header>
            {label('Journey stop')}
            <p>{card.subject.collectible?.campaign_title}</p>
          </header>
          {photo}
          <div className="pe-journey-caption">
            {fact('position') && <span className="pe-route-position">Stop {fact('position')}</span>}
            {title}
          </div>
        </div>
      );
    case 'checkpoint':
      return (
        <div className="pe-front pe-checkpoint">
          {label(card.subject.collectible?.campaign_title || 'Route checkpoint')}
          <div className="pe-checkpoint-marker">
            {fact('position') ? (
              <>
                <small>Stop</small>
                <strong>{fact('position')}</strong>
              </>
            ) : (
              <strong className="pe-checkpoint-word">
                CHECK
                <br />
                POINT
              </strong>
            )}
          </div>
          {title}
          {fact('campaign_title') && <p>{fact('campaign_title')}</p>}
        </div>
      );
    case 'commemorative':
      return (
        <div className="pe-front pe-commemorative">
          {photo}
          <div className="pe-edition-story">
            {label(fact('occasion') || 'Commemorative')}
            {title}
            <p>{fact('issuer') || card.issuer}</p>
          </div>
        </div>
      );
    default:
      return (
        <div className="pe-front pe-private">
          {label(passportKind(kind)?.front || 'Collected record')}
          {identity}
        </div>
      );
  }
}
